import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Copy, Check } from "lucide-react";
import { formatEUR } from "@/api";
import { toast } from "sonner";

function buildTicket(sale, businessName = "AquaLav") {
  const lines = [];
  lines.push(`🚗 *${businessName}* — Ticket de lavado`);
  lines.push("");
  lines.push(`*Servicio:* ${sale.car_type_name}  ${formatEUR(sale.car_price)}`);
  if (sale.extras?.length) {
    lines.push("*Extras:*");
    sale.extras.forEach((e) => lines.push(`  • ${e.name}  +${formatEUR(e.price)}`));
  }
  if (sale.customer_name) lines.push(`*Cliente:* ${sale.customer_name}`);
  lines.push("");
  lines.push(`*Total:* ${formatEUR(sale.total)}`);
  lines.push(`*Pago:* ${sale.payment_method}`);
  lines.push(`*Fecha:* ${new Date(sale.date).toLocaleString("es-ES")}`);
  lines.push("");
  lines.push("¡Gracias por tu visita! 💧✨");
  return lines.join("\n");
}

function cleanPhone(raw) {
  return (raw || "").replace(/[^\d]/g, "");
}

export default function WhatsAppTicketDialog({ open, onClose, sale }) {
  const [phone, setPhone] = useState("");
  const [copied, setCopied] = useState(false);
  if (!sale) return null;

  const message = buildTicket(sale);

  const sendToWhatsApp = () => {
    const clean = cleanPhone(phone);
    const url = clean
      ? `https://wa.me/${clean}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Ticket copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-emerald-100 text-emerald-700">
              <MessageCircle className="w-4 h-4" />
            </span>
            Ticket por WhatsApp
          </DialogTitle>
          <DialogDescription>
            Envía el ticket al cliente. Si dejas el teléfono vacío, podrás elegir el contacto en WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Teléfono del cliente (con prefijo)</Label>
            <Input
              placeholder="Ej: 34612345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="whatsapp-phone-input"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              Formato internacional sin “+”. Ej: <code>34612345678</code> para España.
            </p>
          </div>

          <div>
            <Label className="mb-1.5 block">Mensaje</Label>
            <Textarea
              rows={9}
              readOnly
              value={message}
              className="font-mono text-xs"
              data-testid="whatsapp-message"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={copy}
            className="gap-2"
            data-testid="whatsapp-copy-button"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copiar texto
          </Button>
          <Button
            onClick={sendToWhatsApp}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="whatsapp-send-button"
          >
            <MessageCircle className="w-4 h-4" />
            Abrir en WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
